"use client";

import {
  CUSTOMER_OWNED_SESSION_STORAGE_BARRIER_KEY,
  isCustomerOwnedSessionStorageQuarantined,
  quarantineCustomerOwnedSessionStorage,
} from "@/app/booking/core/customerOwnedSessionStorageBarrier";

import {
  createCustomerFavouriteOperationId,
  isCustomerFavouriteActionRequest,
  type CustomerFavouriteActionIntent,
  type CustomerFavouriteActionRequest,
} from "./customerFavouritesActionProvider";
import type { CustomerFavouritesOwner } from "./domain/customerFavouritesContracts";

const LOCK_SCHEMA_VERSION = 1 as const;
const STORE_SCHEMA_VERSION = 1 as const;
const LOCK_KIND = "customer-favourite-unknown-action-lock" as const;
const STORE_KIND = "customer-favourite-unknown-action-locks" as const;
const IDENTIFIER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/u;
const MAX_LOCKS_PER_CUSTOMER = 32;
const MAX_STORE_CHARACTERS = 32_000;
const EMPTY_CUSTOMER_ACCOUNT_IDS = Object.freeze([]) as readonly string[];

export const CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LOCK_STORAGE_KEY_PREFIX =
  "volera:customer-favourite-unknown-action-locks:v1:";
export const CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LOCK_CHANGE_EVENT =
  "volera:customer-favourite-unknown-action-locks-change";
const CUSTOMER_FAVOURITE_ENVELOPE_WEB_LOCK_PREFIX =
  "volera:customer-favourite-unknown-action-envelope:v1:";
const CUSTOMER_FAVOURITE_TARGET_WEB_LOCK_PREFIX =
  "volera:customer-favourite-action-target:v1:";
export const CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LIFECYCLE_STORAGE_KEY =
  "volera:customer-favourite-unknown-action-lifecycle:v1";
const LIFECYCLE_TOKEN_PATTERN = /^[a-f0-9]{32}$/u;

export interface CustomerFavouriteUnknownActionLock {
  readonly customerAccountId: string;
  readonly owner: Readonly<CustomerFavouritesOwner>;
  readonly locationId: string;
  readonly intent: CustomerFavouriteActionIntent;
  readonly operationId: string;
}

export interface CustomerFavouriteActionArbitration {
  readonly kind: "customer-favourite-action-arbitration";
}

interface StoredCustomerFavouriteUnknownActionLockV1
  extends CustomerFavouriteUnknownActionLock {
  readonly schemaVersion: typeof LOCK_SCHEMA_VERSION;
  readonly kind: typeof LOCK_KIND;
}

interface CustomerFavouriteUnknownActionLockEnvelopeV1 {
  readonly storeVersion: typeof STORE_SCHEMA_VERSION;
  readonly storeKind: typeof STORE_KIND;
  readonly customerAccountId: string;
  readonly entries: readonly StoredCustomerFavouriteUnknownActionLockV1[];
}

type PersistedRead =
  | {
      status: "ready";
      storage: Storage;
      entries: readonly StoredCustomerFavouriteUnknownActionLockV1[];
    }
  | { status: "unavailable" }
  | { status: "invalid" };

type EffectiveReadBlockReason =
  | "customer-storage-quarantined"
  | "customer-lifecycle-transition"
  | "invalid-owner"
  | "invalid-location-id"
  | "persisted-locks-invalid"
  | "lock-conflict";

type EffectiveRead =
  | {
      status: "ready";
      entries: readonly StoredCustomerFavouriteUnknownActionLockV1[];
      persistence: "browser-local" | "memory-fallback";
      persistedRead: PersistedRead;
    }
  | {
      status: "blocked";
      reason: EffectiveReadBlockReason;
    };

export type CustomerFavouriteActionRequestPreparation =
  | {
      status: "ready";
      request: Readonly<CustomerFavouriteActionRequest>;
      retryingUnknownOutcome: boolean;
      persistence: "browser-local";
      arbitration: CustomerFavouriteActionArbitration;
    }
  | {
      status: "blocked";
      reason:
        | EffectiveReadBlockReason
        | "invalid-intent"
        | "unknown-intent-mismatch"
        | "unknown-authority-mismatch"
        | "operation-id-unavailable"
        | "lock-capacity-reached"
        | "lock-write-failed"
        | "atomic-arbitration-unavailable"
        | "atomic-arbitration-failed"
        | "atomic-storage-unavailable"
        | "target-operation-active";
    };

export type PreparedCustomerFavouriteActionRequest = Extract<
  CustomerFavouriteActionRequestPreparation,
  { status: "ready" }
>;
type CustomerFavouriteActionRequestPreparationCore =
  | Omit<PreparedCustomerFavouriteActionRequest, "arbitration">
  | Exclude<CustomerFavouriteActionRequestPreparation, { status: "ready" }>;

interface CustomerFavouriteCrossContextLock {
  readonly name: string;
}

interface CustomerFavouriteCrossContextLockManager {
  request<T>(
    name: string,
    options: {
      readonly mode: "exclusive";
      readonly ifAvailable?: boolean;
      readonly signal?: AbortSignal;
    },
    callback: (
      lock: CustomerFavouriteCrossContextLock | null,
    ) => T | PromiseLike<T>,
  ): Promise<T>;
}

interface HeldTargetLock {
  readonly release: () => void;
}

interface CapturedCustomerFavouriteUnknownActionLifecycle {
  readonly storage: Storage;
  readonly token: string;
}

interface CustomerFavouriteUnknownActionTeardownRecord {
  readonly storage: Storage;
  readonly token: string;
  readonly verifyDevelopmentRecovery?: (status: "active" | "idle") => boolean;
}

export interface CustomerFavouriteUnknownActionTeardownBoundary {
  readonly kind: "customer-favourite-unknown-action-teardown";
}

export interface DevelopmentEmptyCustomerFavouriteUnknownActionRecovery {
  readonly beginTeardown: () => CustomerFavouriteUnknownActionTeardownBoundary | null;
  readonly isActiveTeardownCurrent: () => boolean;
  readonly canReleaseQuarantine: () => boolean;
}

interface ActiveArbitrationRecord {
  readonly request: Readonly<CustomerFavouriteActionRequest>;
  readonly lockManager: CustomerFavouriteCrossContextLockManager;
  readonly targetLock: HeldTargetLock;
  readonly lifecycle: CapturedCustomerFavouriteUnknownActionLifecycle;
  revoked: boolean;
  released: boolean;
  unsubscribe: () => void;
}

const memoryEntriesByStorageKey = new Map<
  string,
  readonly StoredCustomerFavouriteUnknownActionLockV1[]
>();
let observedStorage: Storage | null | undefined;
let retainedTeardownRecord:
  | (CustomerFavouriteUnknownActionTeardownRecord & { inUse: boolean })
  | null = null;
const teardownRecords = new WeakMap<
  CustomerFavouriteUnknownActionTeardownBoundary,
  CustomerFavouriteUnknownActionTeardownRecord
>();
const activeArbitrations = new WeakMap<
  CustomerFavouriteActionArbitration,
  ActiveArbitrationRecord
>();

function isRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasOnlyKeys(
  value: Record<string, unknown>,
  expectedKeys: readonly string[],
) {
  const keys = Object.keys(value);
  return (
    keys.length === expectedKeys.length &&
    keys.every((key) => expectedKeys.includes(key))
  );
}

function isIdentifier(value: unknown): value is string {
  return typeof value === "string" && IDENTIFIER_PATTERN.test(value);
}

function isOwner(value: unknown): value is CustomerFavouritesOwner {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, [
      "customerAccountId",
      "providerId",
      "providerRevision",
      "snapshotId",
      "snapshotRevision",
      "displayRevision",
    ]) &&
    isIdentifier(value.customerAccountId) &&
    isIdentifier(value.providerId) &&
    isIdentifier(value.providerRevision) &&
    isIdentifier(value.snapshotId) &&
    isIdentifier(value.snapshotRevision) &&
    isIdentifier(value.displayRevision)
  );
}

function cloneOwner(owner: CustomerFavouritesOwner) {
  return Object.freeze({
    customerAccountId: owner.customerAccountId,
    providerId: owner.providerId,
    providerRevision: owner.providerRevision,
    snapshotId: owner.snapshotId,
    snapshotRevision: owner.snapshotRevision,
    displayRevision: owner.displayRevision,
  });
}

function targetKey(owner: CustomerFavouritesOwner, locationId: string) {
  return JSON.stringify([
    owner.customerAccountId,
    locationId,
  ]);
}

function ownersMatch(
  first: CustomerFavouritesOwner,
  second: CustomerFavouritesOwner,
) {
  return (
    first.customerAccountId === second.customerAccountId &&
    first.providerId === second.providerId &&
    first.providerRevision === second.providerRevision &&
    first.snapshotId === second.snapshotId &&
    first.snapshotRevision === second.snapshotRevision &&
    first.displayRevision === second.displayRevision
  );
}

function retryAuthorityMatches(
  persistedOwner: CustomerFavouritesOwner,
  currentOwner: CustomerFavouritesOwner,
) {
  return (
    persistedOwner.customerAccountId === currentOwner.customerAccountId &&
    persistedOwner.providerId === currentOwner.providerId &&
    persistedOwner.providerRevision === currentOwner.providerRevision
  );
}

function lockTargetKey(lock: CustomerFavouriteUnknownActionLock) {
  return targetKey(lock.owner, lock.locationId);
}

export function buildCustomerFavouriteUnknownActionLockStorageKey(
  customerAccountId: string,
) {
  return isIdentifier(customerAccountId)
    ? `${CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LOCK_STORAGE_KEY_PREFIX}${encodeURIComponent(customerAccountId)}`
    : null;
}

function getCrossContextLockManager() {
  if (typeof navigator === "undefined") {
    return null;
  }

  const lockManager = (
    navigator as Navigator & {
      locks?: CustomerFavouriteCrossContextLockManager;
    }
  ).locks;
  return lockManager && typeof lockManager.request === "function"
    ? lockManager
    : null;
}

/** Pure capability probe used to keep favourite controls visibly fail-closed. */
export function isCustomerFavouriteCrossContextActionArbitrationAvailable() {
  return getCrossContextLockManager() !== null;
}

function buildEnvelopeWebLockName(customerAccountId: string) {
  return `${CUSTOMER_FAVOURITE_ENVELOPE_WEB_LOCK_PREFIX}${encodeURIComponent(customerAccountId)}`;
}

function buildTargetWebLockName(
  customerAccountId: string,
  locationId: string,
) {
  return `${CUSTOMER_FAVOURITE_TARGET_WEB_LOCK_PREFIX}${encodeURIComponent(customerAccountId)}:${encodeURIComponent(locationId)}`;
}

async function acquireTargetLock(
  lockManager: CustomerFavouriteCrossContextLockManager,
  customerAccountId: string,
  locationId: string,
  signal?: AbortSignal,
): Promise<
  | { status: "acquired"; lock: HeldTargetLock }
  | { status: "active" | "failed" }
> {
  if (signal?.aborted) {
    return { status: "failed" };
  }

  let settleAcquisition!: (
    result:
      | { status: "acquired"; lock: HeldTargetLock }
      | { status: "active" | "failed" },
  ) => void;
  let acquisitionSettled = false;
  const acquisition = new Promise<
    | { status: "acquired"; lock: HeldTargetLock }
    | { status: "active" | "failed" }
  >((resolve) => {
    settleAcquisition = (result) => {
      if (!acquisitionSettled) {
        acquisitionSettled = true;
        resolve(result);
      }
    };
  });
  let releaseHeldLock!: () => void;
  const held = new Promise<void>((resolve) => {
    releaseHeldLock = resolve;
  });

  void lockManager
    .request(
      buildTargetWebLockName(customerAccountId, locationId),
      // Chromium follows the Web Locks contract and rejects a request that
      // combines `ifAvailable` with `signal`. This target probe must remain
      // non-queued, so cancellation is handled inside the callback instead.
      { mode: "exclusive", ifAvailable: true },
      async (lock) => {
        if (!lock) {
          settleAcquisition({ status: "active" });
          return;
        }

        if (signal?.aborted) {
          settleAcquisition({ status: "failed" });
          return;
        }

        let released = false;
        let handleAbort: (() => void) | null = null;
        const targetLock = Object.freeze({
          release() {
            if (!released) {
              released = true;
              if (handleAbort) {
                signal?.removeEventListener("abort", handleAbort);
              }
              releaseHeldLock();
            }
          },
        });
        handleAbort = () => targetLock.release();
        signal?.addEventListener("abort", handleAbort, { once: true });
        settleAcquisition({ status: "acquired", lock: targetLock });
        await held;
      },
    )
    .catch(() => {
      settleAcquisition({ status: "failed" });
    });

  return acquisition;
}

async function withEnvelopeWebLock<T>(
  lockManager: CustomerFavouriteCrossContextLockManager,
  customerAccountId: string,
  signal: AbortSignal | undefined,
  callback: () => T | PromiseLike<T>,
): Promise<{ status: "completed"; value: T } | { status: "failed" }> {
  try {
    return await lockManager.request(
      buildEnvelopeWebLockName(customerAccountId),
      { mode: "exclusive", signal },
      async (lock) =>
        lock
          ? { status: "completed" as const, value: await callback() }
          : { status: "failed" as const },
    );
  } catch {
    return { status: "failed" };
  }
}

function getLocalStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storage = window.localStorage;
    if (
      observedStorage !== undefined &&
      observedStorage !== null &&
      storage !== observedStorage
    ) {
      memoryEntriesByStorageKey.clear();
      retainedTeardownRecord = null;
    }
    observedStorage = storage;
    return storage;
  } catch {
    if (observedStorage === undefined) {
      observedStorage = null;
    }
    return null;
  }
}

type CustomerFavouriteUnknownActionLifecycleState =
  | { readonly status: "idle"; readonly token: string }
  | { readonly status: "active"; readonly token: string };

function parseLifecycleState(
  value: string | null,
): CustomerFavouriteUnknownActionLifecycleState | null {
  if (value === null) {
    return Object.freeze({ status: "idle", token: "initial" });
  }

  const match = /^(idle|active):([a-f0-9]{32})$/u.exec(value);
  if (!match) {
    return null;
  }

  return Object.freeze({
    status: match[1] as "idle" | "active",
    token: match[2],
  });
}

function readLifecycleState(storage: Storage) {
  try {
    return parseLifecycleState(
      storage.getItem(
        CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LIFECYCLE_STORAGE_KEY,
      ),
    );
  } catch {
    return null;
  }
}

function publishLifecycleChange() {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LOCK_CHANGE_EVENT, {
      detail: Object.freeze({ lifecycle: true }),
    }),
  );
}

function createLifecycleToken() {
  if (typeof crypto === "undefined") {
    return null;
  }

  try {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    const token = Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");
    return LIFECYCLE_TOKEN_PATTERN.test(token) ? token : null;
  } catch {
    return null;
  }
}

function captureLifecycle(): CapturedCustomerFavouriteUnknownActionLifecycle | null {
  if (isCustomerOwnedSessionStorageQuarantined()) {
    return null;
  }

  const storage = getLocalStorage();
  if (!storage) {
    return null;
  }

  const state = readLifecycleState(storage);
  return state?.status === "idle"
    ? Object.freeze({ storage, token: state.token })
    : null;
}

function lifecycleIsCurrent(
  expected: CapturedCustomerFavouriteUnknownActionLifecycle,
) {
  if (
    isCustomerOwnedSessionStorageQuarantined() ||
    getLocalStorage() !== expected.storage
  ) {
    return false;
  }

  const state = readLifecycleState(expected.storage);
  return state?.status === "idle" && state.token === expected.token;
}

function teardownRecordIsCurrent(
  record: CustomerFavouriteUnknownActionTeardownRecord,
) {
  if (record.verifyDevelopmentRecovery?.("active") === false) {
    return false;
  }
  if (getLocalStorage() !== record.storage) {
    return false;
  }

  const state = readLifecycleState(record.storage);
  return state?.status === "active" && state.token === record.token;
}

function getTeardownRecord(
  boundary: CustomerFavouriteUnknownActionTeardownBoundary,
) {
  const record = teardownRecords.get(boundary);
  return record && teardownRecordIsCurrent(record) ? record : null;
}

/**
 * Starts the synchronous persistent lifecycle boundary used by central auth
 * teardown. A retained same-runtime tombstone may be resumed after a failed
 * verified clear; an active boundary owned elsewhere remains fail closed.
 */
export function beginCustomerFavouriteUnknownActionTeardown(): CustomerFavouriteUnknownActionTeardownBoundary | null {
  const storage = getLocalStorage();
  if (!storage) {
    return null;
  }

  const currentState = readLifecycleState(storage);
  let token: string | null = null;

  if (currentState?.status === "active") {
    if (
      retainedTeardownRecord &&
      retainedTeardownRecord.storage === storage &&
      retainedTeardownRecord.token === currentState.token &&
      !retainedTeardownRecord.inUse
    ) {
      retainedTeardownRecord.inUse = true;
      token = currentState.token;
    } else {
      return null;
    }
  } else if (currentState?.status === "idle") {
    token = createLifecycleToken();
    if (!token) {
      return null;
    }

    const activeValue = `active:${token}`;
    try {
      storage.setItem(
        CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LIFECYCLE_STORAGE_KEY,
        activeValue,
      );
      if (
        storage.getItem(
          CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LIFECYCLE_STORAGE_KEY,
        ) !== activeValue
      ) {
        return null;
      }
    } catch {
      return null;
    }

    retainedTeardownRecord = { storage, token, inUse: true };
  } else {
    return null;
  }
  if (!token) {
    return null;
  }

  const boundary = Object.freeze({
    kind: "customer-favourite-unknown-action-teardown" as const,
  });
  teardownRecords.set(boundary, Object.freeze({
    storage,
    token,
    ...(retainedTeardownRecord?.verifyDevelopmentRecovery
      ? { verifyDevelopmentRecovery: retainedTeardownRecord.verifyDevelopmentRecovery }
      : {}),
  }));
  publishLifecycleChange();
  return boundary;
}

/**
 * Explicit logout recovery for a development tab left completely empty by a
 * previous runtime. Capture before any cleanup: an active lifecycle with data,
 * an unknown outcome, or a current customer must never qualify through deletion.
 */
export function captureDevelopmentEmptyCustomerFavouriteUnknownActionRecovery({
  storage,
  sessionStorage,
  isAuthSessionAbsent,
}: {
  readonly storage: Storage;
  readonly sessionStorage: Storage;
  readonly isAuthSessionAbsent: () => boolean;
}): DevelopmentEmptyCustomerFavouriteUnknownActionRecovery | null {
  if (process.env.NODE_ENV !== "development") {
    return null;
  }

  const initialState = readLifecycleState(storage);
  if (initialState?.status !== "active") {
    return null;
  }

  let replacementToken: string | null = null;
  let consumed = false;
  const isCurrent = (token: string, status: "active" | "idle") => {
    try {
      if (
        process.env.NODE_ENV !== "development" ||
        typeof window === "undefined" ||
        window.localStorage !== storage ||
        window.sessionStorage !== sessionStorage ||
        (observedStorage != null && observedStorage !== storage) ||
        memoryEntriesByStorageKey.size !== 0 ||
        (retainedTeardownRecord?.inUse &&
          (replacementToken === null ||
            retainedTeardownRecord.storage !== storage ||
            retainedTeardownRecord.token !== replacementToken)) ||
        !isAuthSessionAbsent() ||
        sessionStorage.length !== 0 ||
        sessionStorage.key(0) !== null ||
        storage.getItem(CUSTOMER_OWNED_SESSION_STORAGE_BARRIER_KEY) !== "quarantined" ||
        storage.getItem(CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LIFECYCLE_STORAGE_KEY) !== `${status}:${token}`
      ) {
        return false;
      }

      const storageLength = storage.length;
      for (let index = 0; index < storageLength; index += 1) {
        const storageKey = storage.key(index);
        if (
          storageKey === null ||
          storageKey.startsWith(CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LOCK_STORAGE_KEY_PREFIX)
        ) {
          return false;
        }
      }

      return (
        storage.length === storageLength &&
        window.localStorage === storage &&
        window.sessionStorage === sessionStorage &&
        isAuthSessionAbsent() &&
        sessionStorage.length === 0 &&
        sessionStorage.key(0) === null &&
        memoryEntriesByStorageKey.size === 0 &&
        storage.getItem(CUSTOMER_OWNED_SESSION_STORAGE_BARRIER_KEY) === "quarantined" &&
        storage.getItem(CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LIFECYCLE_STORAGE_KEY) === `${status}:${token}`
      );
    } catch {
      return false;
    }
  };

  if (!isCurrent(initialState.token, "active")) {
    return null;
  }

  const isActiveTeardownCurrent = () =>
    replacementToken !== null && isCurrent(replacementToken, "active");

  return Object.freeze({
    beginTeardown() {
      if (consumed || !isCurrent(initialState.token, "active")) {
        return null;
      }
      consumed = true;
      const token = createLifecycleToken();
      if (!token || token === initialState.token || !isCurrent(initialState.token, "active")) {
        return null;
      }

      try {
        storage.setItem(CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LIFECYCLE_STORAGE_KEY, `active:${token}`);
        replacementToken = token;
        if (!isActiveTeardownCurrent()) {
          return null;
        }
      } catch {
        return null;
      }

      const record = Object.freeze({
        storage,
        token,
        verifyDevelopmentRecovery: (status: "active" | "idle") =>
          replacementToken !== null && isCurrent(replacementToken, status),
      });
      const boundary = Object.freeze({
        kind: "customer-favourite-unknown-action-teardown" as const,
      });
      retainedTeardownRecord = { ...record, inUse: true };
      teardownRecords.set(boundary, record);
      publishLifecycleChange();
      return boundary;
    },
    isActiveTeardownCurrent,
    canReleaseQuarantine: () =>
      replacementToken !== null && isCurrent(replacementToken, "idle"),
  });
}

/** Keeps a failed teardown tombstone active while allowing a verified retry. */
export function retainCustomerFavouriteUnknownActionTeardown(
  boundary: CustomerFavouriteUnknownActionTeardownBoundary,
) {
  const record = teardownRecords.get(boundary);
  teardownRecords.delete(boundary);
  if (!record || !teardownRecordIsCurrent(record)) {
    retainedTeardownRecord = null;
    return false;
  }

  if (
    retainedTeardownRecord?.storage === record.storage &&
    retainedTeardownRecord.token === record.token
  ) {
    retainedTeardownRecord.inUse = false;
  }
  return true;
}

/**
 * Releases only the exact active token after auth identity replacement/removal
 * and every registered customer-owned clear have both been verified.
 */
export function completeCustomerFavouriteUnknownActionTeardown(
  boundary: CustomerFavouriteUnknownActionTeardownBoundary,
) {
  const record = teardownRecords.get(boundary);
  if (!record || !teardownRecordIsCurrent(record)) {
    teardownRecords.delete(boundary);
    return false;
  }

  const activeValue = `active:${record.token}`;
  const idleValue = `idle:${record.token}`;
  let completed = false;
  try {
    if (
      record.storage.getItem(
        CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LIFECYCLE_STORAGE_KEY,
      ) !== activeValue
    ) {
      throw new Error("Customer lifecycle ownership changed.");
    }
    record.storage.setItem(
      CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LIFECYCLE_STORAGE_KEY,
      idleValue,
    );
    if (
      record.storage.getItem(
        CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LIFECYCLE_STORAGE_KEY,
      ) !== idleValue
    ) {
      throw new Error("Customer lifecycle release was not verified.");
    }
    completed = true;
  } catch {
    completed = false;
  }
  teardownRecords.delete(boundary);

  if (!completed) {
    if (
      retainedTeardownRecord?.storage === record.storage &&
      retainedTeardownRecord.token === record.token
    ) {
      retainedTeardownRecord.inUse = false;
    }
    return false;
  }

  retainedTeardownRecord = null;
  publishLifecycleChange();
  return record.verifyDevelopmentRecovery?.("idle") !== false;
}

function validateStoredLock(
  value: unknown,
  expectedCustomerAccountId: string,
): StoredCustomerFavouriteUnknownActionLockV1 | null {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, [
      "schemaVersion",
      "kind",
      "customerAccountId",
      "owner",
      "locationId",
      "intent",
      "operationId",
    ]) ||
    value.schemaVersion !== LOCK_SCHEMA_VERSION ||
    value.kind !== LOCK_KIND ||
    value.customerAccountId !== expectedCustomerAccountId ||
    !isOwner(value.owner) ||
    value.owner.customerAccountId !== expectedCustomerAccountId ||
    !isIdentifier(value.locationId) ||
    (value.intent !== "add" && value.intent !== "remove")
  ) {
    return null;
  }

  const request = {
    operationId: value.operationId,
    owner: value.owner,
    locationId: value.locationId,
    intent: value.intent,
  };
  if (!isCustomerFavouriteActionRequest(request)) {
    return null;
  }

  return Object.freeze({
    schemaVersion: LOCK_SCHEMA_VERSION,
    kind: LOCK_KIND,
    customerAccountId: expectedCustomerAccountId,
    owner: cloneOwner(request.owner),
    locationId: request.locationId,
    intent: request.intent,
    operationId: request.operationId,
  });
}

function parseEnvelope(
  raw: string | null,
  customerAccountId: string,
):
  | { status: "ready"; entries: readonly StoredCustomerFavouriteUnknownActionLockV1[] }
  | { status: "invalid" } {
  if (raw === null) {
    return { status: "ready", entries: [] };
  }
  if (raw.length > MAX_STORE_CHARACTERS) {
    return { status: "invalid" };
  }

  try {
    const value = JSON.parse(raw) as unknown;
    if (
      !isRecord(value) ||
      !hasOnlyKeys(value, [
        "storeVersion",
        "storeKind",
        "customerAccountId",
        "entries",
      ]) ||
      value.storeVersion !== STORE_SCHEMA_VERSION ||
      value.storeKind !== STORE_KIND ||
      value.customerAccountId !== customerAccountId ||
      !Array.isArray(value.entries) ||
      value.entries.length > MAX_LOCKS_PER_CUSTOMER
    ) {
      return { status: "invalid" };
    }

    const entries: StoredCustomerFavouriteUnknownActionLockV1[] = [];
    const seenTargets = new Set<string>();
    for (const candidate of value.entries) {
      const lock = validateStoredLock(candidate, customerAccountId);
      if (!lock) {
        return { status: "invalid" };
      }
      const key = lockTargetKey(lock);
      if (seenTargets.has(key)) {
        return { status: "invalid" };
      }
      seenTargets.add(key);
      entries.push(lock);
    }

    return { status: "ready", entries: Object.freeze(entries) };
  } catch {
    return { status: "invalid" };
  }
}

function readPersisted(customerAccountId: string): PersistedRead {
  const storageKey =
    buildCustomerFavouriteUnknownActionLockStorageKey(customerAccountId);
  const storage = getLocalStorage();
  if (!storageKey || !storage) {
    return { status: "unavailable" };
  }

  try {
    const parsed = parseEnvelope(
      storage.getItem(storageKey),
      customerAccountId,
    );
    return parsed.status === "ready"
      ? { status: "ready", storage, entries: parsed.entries }
      : parsed;
  } catch {
    return { status: "unavailable" };
  }
}

function hasExactPersistedActionRequest(
  request: Readonly<CustomerFavouriteActionRequest>,
  expectedLifecycle = captureLifecycle(),
) {
  if (
    !expectedLifecycle ||
    !lifecycleIsCurrent(expectedLifecycle) ||
    !isCustomerFavouriteActionRequest(request)
  ) {
    return false;
  }

  const persisted = readPersisted(request.owner.customerAccountId);
  if (
    persisted.status !== "ready" ||
    persisted.storage !== expectedLifecycle.storage ||
    !lifecycleIsCurrent(expectedLifecycle)
  ) {
    return false;
  }

  const key = targetKey(request.owner, request.locationId);
  const lock = persisted.entries.find(
    (entry) => lockTargetKey(entry) === key,
  );
  const matches = Boolean(
    lock &&
      lock.operationId === request.operationId &&
      lock.intent === request.intent &&
      ownersMatch(lock.owner, request.owner),
  );
  return matches && lifecycleIsCurrent(expectedLifecycle);
}

function createActionArbitration(
  request: Readonly<CustomerFavouriteActionRequest>,
  lockManager: CustomerFavouriteCrossContextLockManager,
  targetLock: HeldTargetLock,
  lifecycle: CapturedCustomerFavouriteUnknownActionLifecycle,
) {
  const arbitration = Object.freeze({
    kind: "customer-favourite-action-arbitration" as const,
  });
  const record: ActiveArbitrationRecord = {
    request,
    lockManager,
    targetLock,
    lifecycle,
    revoked: false,
    released: false,
    unsubscribe: () => undefined,
  };
  record.unsubscribe = subscribeCustomerFavouriteUnknownActionLocks(() => {
    if (!hasExactPersistedActionRequest(request, lifecycle)) {
      record.revoked = true;
    }
  });
  activeArbitrations.set(arbitration, record);
  return arbitration;
}

function requestsMatch(
  first: Readonly<CustomerFavouriteActionRequest>,
  second: Readonly<CustomerFavouriteActionRequest>,
) {
  return (
    first.operationId === second.operationId &&
    first.locationId === second.locationId &&
    first.intent === second.intent &&
    ownersMatch(first.owner, second.owner) &&
    targetKey(first.owner, first.locationId) ===
      targetKey(second.owner, second.locationId)
  );
}

function serializeEnvelope(
  customerAccountId: string,
  entries: readonly StoredCustomerFavouriteUnknownActionLockV1[],
) {
  return JSON.stringify({
    storeVersion: STORE_SCHEMA_VERSION,
    storeKind: STORE_KIND,
    customerAccountId,
    entries,
  } satisfies CustomerFavouriteUnknownActionLockEnvelopeV1);
}

function discardExactSerializedEnvelope(
  storage: Storage,
  storageKey: string,
  serialized: string,
) {
  try {
    if (storage.getItem(storageKey) !== serialized) {
      return true;
    }
    storage.removeItem(storageKey);
    return storage.getItem(storageKey) !== serialized;
  } catch {
    return false;
  }
}

function restoreExactSerializedEnvelope(
  storage: Storage,
  storageKey: string,
  serialized: string,
  previousRaw: string | null,
) {
  try {
    if (storage.getItem(storageKey) !== serialized) {
      return true;
    }
    if (previousRaw === null) {
      storage.removeItem(storageKey);
    } else {
      storage.setItem(storageKey, previousRaw);
    }
    return storage.getItem(storageKey) === previousRaw;
  } catch {
    return false;
  }
}

function persistEntries(
  storage: Storage,
  customerAccountId: string,
  entries: readonly StoredCustomerFavouriteUnknownActionLockV1[],
  expectedLifecycle: CapturedCustomerFavouriteUnknownActionLifecycle,
) {
  const storageKey =
    buildCustomerFavouriteUnknownActionLockStorageKey(customerAccountId);
  if (!storageKey) {
    return false;
  }

  const serialized = serializeEnvelope(customerAccountId, entries);
  if (serialized.length > MAX_STORE_CHARACTERS) {
    return false;
  }

  if (
    storage !== expectedLifecycle.storage ||
    !lifecycleIsCurrent(expectedLifecycle)
  ) {
    return false;
  }

  let wroteSerialized = false;
  try {
    const previousRaw = storage.getItem(storageKey);
    if (!lifecycleIsCurrent(expectedLifecycle)) {
      return false;
    }

    if (entries.length === 0) {
      storage.removeItem(storageKey);
    } else {
      storage.setItem(storageKey, serialized);
      wroteSerialized = true;
    }

    const writtenRaw = storage.getItem(storageKey);
    if (!lifecycleIsCurrent(expectedLifecycle)) {
      if (
        wroteSerialized &&
        writtenRaw === serialized &&
        !discardExactSerializedEnvelope(storage, storageKey, serialized)
      ) {
        quarantineCustomerOwnedSessionStorage();
      }
      return false;
    }

    const confirmation = parseEnvelope(
      writtenRaw,
      customerAccountId,
    );
    const confirmed =
      confirmation.status === "ready" &&
      JSON.stringify(confirmation.entries) === JSON.stringify(entries) &&
      lifecycleIsCurrent(expectedLifecycle);
    if (confirmed) {
      return true;
    }

    if (
      wroteSerialized &&
      !restoreExactSerializedEnvelope(
        storage,
        storageKey,
        serialized,
        previousRaw,
      )
    ) {
      quarantineCustomerOwnedSessionStorage();
    }
    return false;
  } catch {
    if (
      wroteSerialized &&
      !discardExactSerializedEnvelope(storage, storageKey, serialized)
    ) {
      quarantineCustomerOwnedSessionStorage();
    }
    return false;
  }
}

function publishChange(customerAccountId: string) {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LOCK_CHANGE_EVENT, {
      detail: Object.freeze({ customerAccountId }),
    }),
  );
}

function mergeEntries(
  persisted: readonly StoredCustomerFavouriteUnknownActionLockV1[],
  memory: readonly StoredCustomerFavouriteUnknownActionLockV1[],
) {
  const entries = [...persisted];
  const byTarget = new Map(entries.map((entry) => [lockTargetKey(entry), entry]));

  for (const lock of memory) {
    const existing = byTarget.get(lockTargetKey(lock));
    if (existing) {
      if (
        existing.operationId !== lock.operationId ||
        existing.intent !== lock.intent ||
        !ownersMatch(existing.owner, lock.owner)
      ) {
        return null;
      }
      continue;
    }
    entries.push(lock);
    byTarget.set(lockTargetKey(lock), lock);
  }

  return entries.length <= MAX_LOCKS_PER_CUSTOMER
    ? (Object.freeze(entries) as readonly StoredCustomerFavouriteUnknownActionLockV1[])
    : null;
}

function readEffective(
  owner: CustomerFavouritesOwner,
  locationId: string,
  expectedLifecycle = captureLifecycle(),
): EffectiveRead {
  if (isCustomerOwnedSessionStorageQuarantined()) {
    return { status: "blocked", reason: "customer-storage-quarantined" };
  }
  if (!isOwner(owner)) {
    return { status: "blocked", reason: "invalid-owner" };
  }
  if (!isIdentifier(locationId)) {
    return { status: "blocked", reason: "invalid-location-id" };
  }
  if (!expectedLifecycle || !lifecycleIsCurrent(expectedLifecycle)) {
    return { status: "blocked", reason: "customer-lifecycle-transition" };
  }

  const storageKey =
    buildCustomerFavouriteUnknownActionLockStorageKey(
      owner.customerAccountId,
    ) as string;
  const memory = memoryEntriesByStorageKey.get(storageKey) ?? [];
  const persistedRead = readPersisted(owner.customerAccountId);
  if (!lifecycleIsCurrent(expectedLifecycle)) {
    return { status: "blocked", reason: "customer-lifecycle-transition" };
  }
  if (persistedRead.status === "invalid") {
    return { status: "blocked", reason: "persisted-locks-invalid" };
  }

  if (persistedRead.status === "unavailable") {
    return {
      status: "ready",
      entries: memory,
      persistence: "memory-fallback",
      persistedRead,
    };
  }

  const merged = mergeEntries(persistedRead.entries, memory);
  if (!merged) {
    return { status: "blocked", reason: "lock-conflict" };
  }

  if (memory.length > 0 && persistEntries(
    persistedRead.storage,
    owner.customerAccountId,
    merged,
    expectedLifecycle,
  )) {
    memoryEntriesByStorageKey.delete(storageKey);
    return {
      status: "ready",
      entries: merged,
      persistence: "browser-local",
      persistedRead,
    };
  }

  if (!lifecycleIsCurrent(expectedLifecycle)) {
    memoryEntriesByStorageKey.delete(storageKey);
    return { status: "blocked", reason: "customer-lifecycle-transition" };
  }

  return {
    status: "ready",
    entries: merged,
    persistence:
      memory.length > 0 ? "memory-fallback" : "browser-local",
    persistedRead,
  };
}

function toStoredLock(
  request: CustomerFavouriteActionRequest,
): StoredCustomerFavouriteUnknownActionLockV1 {
  return Object.freeze({
    schemaVersion: LOCK_SCHEMA_VERSION,
    kind: LOCK_KIND,
    customerAccountId: request.owner.customerAccountId,
    owner: cloneOwner(request.owner),
    locationId: request.locationId,
    intent: request.intent,
    operationId: request.operationId,
  });
}

function toPublicLock(
  lock: StoredCustomerFavouriteUnknownActionLockV1,
): CustomerFavouriteUnknownActionLock {
  return Object.freeze({
    customerAccountId: lock.customerAccountId,
    owner: lock.owner,
    locationId: lock.locationId,
    intent: lock.intent,
    operationId: lock.operationId,
  });
}

function storeValidatedRequest(
  request: CustomerFavouriteActionRequest,
  expectedLifecycle = captureLifecycle(),
) {
  if (!expectedLifecycle) {
    return null;
  }
  const effective = readEffective(
    request.owner,
    request.locationId,
    expectedLifecycle,
  );
  if (effective.status === "blocked") {
    return null;
  }

  const key = targetKey(request.owner, request.locationId);
  const existing = effective.entries.find(
    (entry) => lockTargetKey(entry) === key,
  );
  if (existing) {
    return existing.operationId === request.operationId &&
      existing.intent === request.intent &&
      ownersMatch(existing.owner, request.owner)
      ? {
          lock: toPublicLock(existing),
          persistence: effective.persistence,
        }
      : null;
  }
  if (effective.entries.length >= MAX_LOCKS_PER_CUSTOMER) {
    return null;
  }

  const lock = toStoredLock(request);
  const nextEntries = Object.freeze([...effective.entries, lock]);
  if (
    serializeEnvelope(
      request.owner.customerAccountId,
      nextEntries,
    ).length > MAX_STORE_CHARACTERS
  ) {
    return null;
  }
  const storageKey =
    buildCustomerFavouriteUnknownActionLockStorageKey(
      request.owner.customerAccountId,
    ) as string;

  if (
    effective.persistedRead.status === "ready" &&
    persistEntries(
      effective.persistedRead.storage,
      request.owner.customerAccountId,
      nextEntries,
      expectedLifecycle,
    )
  ) {
    memoryEntriesByStorageKey.delete(storageKey);
    publishChange(request.owner.customerAccountId);
    return {
      lock: toPublicLock(lock),
      persistence: "browser-local" as const,
    };
  }

  if (!lifecycleIsCurrent(expectedLifecycle)) {
    memoryEntriesByStorageKey.delete(storageKey);
    publishChange(request.owner.customerAccountId);
    return null;
  }

  memoryEntriesByStorageKey.set(storageKey, nextEntries);
  publishChange(request.owner.customerAccountId);
  return {
    lock: toPublicLock(lock),
    persistence: "memory-fallback" as const,
  };
}

export function getCustomerFavouriteUnknownActionLock(
  owner: CustomerFavouritesOwner,
  locationId: string,
) {
  const effective = readEffective(owner, locationId);
  if (effective.status === "blocked") {
    return null;
  }

  const key = targetKey(owner, locationId);
  const lock = effective.entries.find(
    (entry) => lockTargetKey(entry) === key,
  );
  return lock ? toPublicLock(lock) : null;
}

/**
 * Persists the absorbing preflight guard. Callers must do this before invoking
 * any provider whose outcome could become ambiguous.
 */
export function setCustomerFavouriteUnknownActionLock(
  request: CustomerFavouriteActionRequest,
) {
  if (
    isCustomerOwnedSessionStorageQuarantined() ||
    !isCustomerFavouriteActionRequest(request)
  ) {
    return null;
  }

  const lifecycle = captureLifecycle();
  return lifecycle
    ? storeValidatedRequest(request, lifecycle)?.lock ?? null
    : null;
}

function prepareCustomerFavouriteActionRequestUnderLock(
  owner: CustomerFavouritesOwner,
  locationId: string,
  intent: CustomerFavouriteActionIntent,
  expectedLifecycle: CapturedCustomerFavouriteUnknownActionLifecycle,
): CustomerFavouriteActionRequestPreparationCore {
  const effective = readEffective(owner, locationId, expectedLifecycle);
  if (effective.status === "blocked") {
    return effective;
  }
  if (
    effective.persistence !== "browser-local" ||
    effective.persistedRead.status !== "ready"
  ) {
    return { status: "blocked", reason: "atomic-storage-unavailable" };
  }
  const key = targetKey(owner, locationId);
  const existing = effective.entries.find(
    (entry) => lockTargetKey(entry) === key,
  );
  if (existing) {
    if (existing.intent !== intent) {
      return { status: "blocked", reason: "unknown-intent-mismatch" };
    }
    if (!retryAuthorityMatches(existing.owner, owner)) {
      return { status: "blocked", reason: "unknown-authority-mismatch" };
    }
    return {
      status: "ready",
      request: Object.freeze({
        operationId: existing.operationId,
        owner: existing.owner,
        locationId: existing.locationId,
        intent: existing.intent,
      }),
      retryingUnknownOutcome: true,
      persistence: "browser-local",
    };
  }
  if (effective.entries.length >= MAX_LOCKS_PER_CUSTOMER) {
    return { status: "blocked", reason: "lock-capacity-reached" };
  }

  const operationId = createCustomerFavouriteOperationId();
  if (!operationId) {
    return { status: "blocked", reason: "operation-id-unavailable" };
  }
  const request = Object.freeze({
    operationId,
    owner: cloneOwner(owner),
    locationId,
    intent,
  });
  const stored = storeValidatedRequest(request, expectedLifecycle);
  return stored?.persistence === "browser-local"
    ? {
        status: "ready",
        request,
        retryingUnknownOutcome: false,
        persistence: "browser-local",
      }
    : { status: "blocked", reason: "lock-write-failed" };
}

export async function prepareCustomerFavouriteActionRequest(
  owner: CustomerFavouritesOwner,
  locationId: string,
  intent: CustomerFavouriteActionIntent,
  signal?: AbortSignal,
): Promise<CustomerFavouriteActionRequestPreparation> {
  if (!isOwner(owner)) {
    return { status: "blocked", reason: "invalid-owner" };
  }
  if (!isIdentifier(locationId)) {
    return { status: "blocked", reason: "invalid-location-id" };
  }
  if (intent !== "add" && intent !== "remove") {
    return { status: "blocked", reason: "invalid-intent" };
  }
  if (isCustomerOwnedSessionStorageQuarantined()) {
    return { status: "blocked", reason: "customer-storage-quarantined" };
  }

  const lifecycle = captureLifecycle();
  if (!lifecycle) {
    return { status: "blocked", reason: "customer-lifecycle-transition" };
  }

  const lockManager = getCrossContextLockManager();
  if (!lockManager) {
    return { status: "blocked", reason: "atomic-arbitration-unavailable" };
  }
  const targetLockResult = await acquireTargetLock(
    lockManager,
    owner.customerAccountId,
    locationId,
    signal,
  );
  if (targetLockResult.status !== "acquired") {
    return {
      status: "blocked",
      reason:
        targetLockResult.status === "active"
          ? "target-operation-active"
          : "atomic-arbitration-failed",
    };
  }

  const prepared = await withEnvelopeWebLock(
    lockManager,
    owner.customerAccountId,
    signal,
    () =>
      prepareCustomerFavouriteActionRequestUnderLock(
        owner,
        locationId,
        intent,
        lifecycle,
      ),
  );
  if (prepared.status !== "completed") {
    targetLockResult.lock.release();
    return { status: "blocked", reason: "atomic-arbitration-failed" };
  }
  if (prepared.value.status !== "ready") {
    targetLockResult.lock.release();
    return prepared.value;
  }
  if (!lifecycleIsCurrent(lifecycle)) {
    targetLockResult.lock.release();
    return { status: "blocked", reason: "customer-lifecycle-transition" };
  }

  const arbitration = createActionArbitration(
    prepared.value.request,
    lockManager,
    targetLockResult.lock,
    lifecycle,
  );
  return Object.freeze({
    ...prepared.value,
    arbitration,
  });
}

export function isCustomerFavouriteActionArbitrationCurrent(
  prepared: PreparedCustomerFavouriteActionRequest,
) {
  const record = activeArbitrations.get(prepared.arbitration);
  if (
    !record ||
    record.released ||
    record.revoked ||
    !requestsMatch(record.request, prepared.request) ||
    !hasExactPersistedActionRequest(prepared.request, record.lifecycle)
  ) {
    if (record) {
      record.revoked = true;
    }
    return false;
  }

  return true;
}

export async function verifyCustomerFavouriteActionArbitration(
  prepared: PreparedCustomerFavouriteActionRequest,
  signal?: AbortSignal,
) {
  const record = activeArbitrations.get(prepared.arbitration);
  if (
    !record ||
    record.released ||
    record.revoked ||
    !requestsMatch(record.request, prepared.request)
  ) {
    return false;
  }

  const verification = await withEnvelopeWebLock(
    record.lockManager,
    prepared.request.owner.customerAccountId,
    signal,
    () => hasExactPersistedActionRequest(prepared.request, record.lifecycle),
  );
  const verified =
    verification.status === "completed" && verification.value === true;
  if (
    !verified &&
    !hasExactPersistedActionRequest(prepared.request, record.lifecycle)
  ) {
    record.revoked = true;
  }
  return verified;
}

export function releaseCustomerFavouriteActionArbitration(
  prepared: PreparedCustomerFavouriteActionRequest,
) {
  const record = activeArbitrations.get(prepared.arbitration);
  if (!record || record.released) {
    return false;
  }

  record.released = true;
  record.unsubscribe();
  record.targetLock.release();
  activeArbitrations.delete(prepared.arbitration);
  return true;
}

export async function clearCustomerFavouriteUnknownActionLock(
  owner: CustomerFavouritesOwner,
  locationId: string,
  operationId?: string,
  arbitration?: CustomerFavouriteActionArbitration,
  signal?: AbortSignal,
): Promise<boolean> {
  if (
    !operationId ||
    !arbitration ||
    isCustomerOwnedSessionStorageQuarantined()
  ) {
    return false;
  }
  const record = activeArbitrations.get(arbitration);
  const requestedClear = {
    operationId,
    owner,
    locationId,
    intent: record?.request.intent,
  };
  if (
    !record ||
    record.released ||
    record.revoked ||
    !isCustomerFavouriteActionRequest(requestedClear) ||
    !requestsMatch(record.request, requestedClear)
  ) {
    return false;
  }
  const storageKey =
    buildCustomerFavouriteUnknownActionLockStorageKey(
      owner.customerAccountId,
    ) as string;
  const clearResult = await withEnvelopeWebLock(
    record.lockManager,
    owner.customerAccountId,
    signal,
    () => {
      if (record.revoked || !lifecycleIsCurrent(record.lifecycle)) {
        return false;
      }
      const persisted = readPersisted(owner.customerAccountId);
      if (persisted.status !== "ready") {
        return false;
      }
      const key = targetKey(owner, locationId);
      const existing = persisted.entries.find(
        (entry) => lockTargetKey(entry) === key,
      );
      if (
        !existing ||
        existing.operationId !== operationId ||
        existing.intent !== record.request.intent ||
        !ownersMatch(existing.owner, record.request.owner)
      ) {
        record.revoked = true;
        return false;
      }

      const nextEntries = persisted.entries.filter(
        (entry) => lockTargetKey(entry) !== key,
      );
      if (
        !persistEntries(
          persisted.storage,
          owner.customerAccountId,
          nextEntries,
          record.lifecycle,
        )
      ) {
        return false;
      }

      const memoryEntries = memoryEntriesByStorageKey.get(storageKey);
      if (memoryEntries) {
        const nextMemoryEntries = memoryEntries.filter(
          (entry) => lockTargetKey(entry) !== key,
        );
        if (nextMemoryEntries.length === 0) {
          memoryEntriesByStorageKey.delete(storageKey);
        } else {
          memoryEntriesByStorageKey.set(
            storageKey,
            Object.freeze(nextMemoryEntries),
          );
        }
      }
      return true;
    },
  );
  const cleared =
    clearResult.status === "completed" && clearResult.value === true;
  if (cleared) {
    publishChange(owner.customerAccountId);
  }
  return cleared;
}

function resolveTeardownBoundary(
  boundary?: CustomerFavouriteUnknownActionTeardownBoundary,
):
  | {
      readonly boundary: CustomerFavouriteUnknownActionTeardownBoundary;
      readonly record: CustomerFavouriteUnknownActionTeardownRecord;
      readonly ownedByClear: boolean;
    }
  | null {
  const resolvedBoundary =
    boundary ?? beginCustomerFavouriteUnknownActionTeardown();
  if (!resolvedBoundary) {
    return null;
  }

  const record = getTeardownRecord(resolvedBoundary);
  if (!record) {
    if (!boundary) {
      retainCustomerFavouriteUnknownActionTeardown(resolvedBoundary);
    }
    return null;
  }

  return Object.freeze({
    boundary: resolvedBoundary,
    record,
    ownedByClear: boundary === undefined,
  });
}

function settleOwnedTeardownBoundary(
  scope: NonNullable<ReturnType<typeof resolveTeardownBoundary>>,
  cleared: boolean,
) {
  if (!scope.ownedByClear) {
    return cleared && teardownRecordIsCurrent(scope.record);
  }

  if (!cleared) {
    retainCustomerFavouriteUnknownActionTeardown(scope.boundary);
    return false;
  }

  return completeCustomerFavouriteUnknownActionTeardown(scope.boundary);
}

/**
 * Customer-auth teardown uses the verified boolean. A failed or unverifiable
 * localStorage removal must cause the central customer-owned barrier to remain
 * quarantined.
 */
export function clearCustomerFavouriteUnknownActionLocks(
  customerAccountId: string,
  boundary?: CustomerFavouriteUnknownActionTeardownBoundary,
) {
  const storageKey =
    buildCustomerFavouriteUnknownActionLockStorageKey(customerAccountId);
  if (!storageKey) {
    return false;
  }

  const scope = resolveTeardownBoundary(boundary);
  if (!scope) {
    memoryEntriesByStorageKey.delete(storageKey);
    publishChange(customerAccountId);
    return false;
  }

  let cleared = false;
  try {
    if (!teardownRecordIsCurrent(scope.record)) {
      throw new Error("Customer lifecycle teardown is not current.");
    }

    scope.record.storage.removeItem(storageKey);
    cleared =
      scope.record.storage.getItem(storageKey) === null &&
      teardownRecordIsCurrent(scope.record);
  } catch {
    cleared = false;
  }

  if (cleared) {
    memoryEntriesByStorageKey.delete(storageKey);
    publishChange(customerAccountId);
  }
  return settleOwnedTeardownBoundary(scope, cleared);
}

function collectPersistedUnknownActionLockStorageKeys(storage: Storage) {
  const storageKeys: string[] = [];

  try {
    for (let index = 0; index < storage.length; index += 1) {
      const storageKey = storage.key(index);
      if (
        storageKey?.startsWith(
          CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LOCK_STORAGE_KEY_PREFIX,
        )
      ) {
        storageKeys.push(storageKey);
      }
    }

    return storageKeys;
  } catch {
    return null;
  }
}

function getCustomerAccountIdFromUnknownActionLockStorageKey(
  storageKey: string,
) {
  if (
    !storageKey.startsWith(
      CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LOCK_STORAGE_KEY_PREFIX,
    )
  ) {
    return null;
  }

  try {
    const customerAccountId = decodeURIComponent(
      storageKey.slice(
        CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LOCK_STORAGE_KEY_PREFIX.length,
      ),
    );
    return buildCustomerFavouriteUnknownActionLockStorageKey(
      customerAccountId,
    ) === storageKey
      ? customerAccountId
      : null;
  } catch {
    return null;
  }
}

export interface ClearAllCustomerFavouriteUnknownActionLocksResult {
  readonly cleared: boolean;
  readonly customerAccountIds: readonly string[];
}

/**
 * Exhaustive unknown-auth teardown. Storage keys are discovered by the strict
 * store prefix rather than by preview state, because an uncertain provider
 * attempt may be the customer's only remaining Favourites artifact. Corrupt
 * envelopes are removed as opaque customer-owned data and every removal plus
 * the final empty sweep is verified before `cleared` can become true.
 */
export function clearAllCustomerFavouriteUnknownActionLocks(
  boundary?: CustomerFavouriteUnknownActionTeardownBoundary,
): ClearAllCustomerFavouriteUnknownActionLocksResult {
  if (typeof window === "undefined") {
    return Object.freeze({
      cleared: false,
      customerAccountIds: EMPTY_CUSTOMER_ACCOUNT_IDS,
    });
  }

  const scope = resolveTeardownBoundary(boundary);
  const memoryStorageKeys = [...memoryEntriesByStorageKey.keys()];
  const customerAccountIds = new Set<string>();
  const finish = (cleared: boolean) =>
    Object.freeze({
      cleared: scope
        ? settleOwnedTeardownBoundary(scope, cleared)
        : false,
      customerAccountIds: Object.freeze([...customerAccountIds]),
    });
  const collectCustomerAccountId = (storageKey: string) => {
    const customerAccountId =
      getCustomerAccountIdFromUnknownActionLockStorageKey(storageKey);
    if (customerAccountId) {
      customerAccountIds.add(customerAccountId);
    }
    return customerAccountId;
  };

  for (const storageKey of memoryStorageKeys) {
    collectCustomerAccountId(storageKey);
  }

  if (!scope) {
    return finish(false);
  }

  const storage = scope.record.storage;

  const persistedStorageKeys =
    collectPersistedUnknownActionLockStorageKeys(storage);
  if (!persistedStorageKeys || !teardownRecordIsCurrent(scope.record)) {
    return finish(false);
  }

  const storageKeys = new Set([
    ...persistedStorageKeys,
    ...memoryStorageKeys,
  ]);
  let cleared = true;

  for (const storageKey of storageKeys) {
    const customerAccountId = collectCustomerAccountId(storageKey);

    try {
      if (!teardownRecordIsCurrent(scope.record)) {
        throw new Error("Customer lifecycle teardown is not current.");
      }
      storage.removeItem(storageKey);
      if (
        storage.getItem(storageKey) !== null ||
        !teardownRecordIsCurrent(scope.record)
      ) {
        throw new Error(
          "Customer Favourite unknown-action lock removal was not verified.",
        );
      }

      memoryEntriesByStorageKey.delete(storageKey);
      if (customerAccountId) {
        publishChange(customerAccountId);
      }
    } catch {
      cleared = false;
    }
  }

  const remainingStorageKeys =
    collectPersistedUnknownActionLockStorageKeys(storage);
  if (
    !remainingStorageKeys ||
    !teardownRecordIsCurrent(scope.record)
  ) {
    cleared = false;
  } else if (remainingStorageKeys.length > 0) {
    cleared = false;
    for (const storageKey of remainingStorageKeys) {
      collectCustomerAccountId(storageKey);
    }
  }

  if (memoryEntriesByStorageKey.size > 0) {
    cleared = false;
    for (const storageKey of memoryEntriesByStorageKey.keys()) {
      collectCustomerAccountId(storageKey);
    }
  }

  return finish(cleared);
}

export function subscribeCustomerFavouriteUnknownActionLocks(
  listener: () => void,
) {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const onSameTabChange = () => listener();
  const onStorage = (event: StorageEvent) => {
    if (
      event.key === null ||
      event.key ===
        CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LIFECYCLE_STORAGE_KEY ||
      event.key === CUSTOMER_OWNED_SESSION_STORAGE_BARRIER_KEY ||
      event.key.startsWith(
        CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LOCK_STORAGE_KEY_PREFIX,
      )
    ) {
      listener();
    }
  };
  window.addEventListener(
    CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LOCK_CHANGE_EVENT,
    onSameTabChange,
  );
  window.addEventListener("storage", onStorage);

  return () => {
    window.removeEventListener(
      CUSTOMER_FAVOURITE_UNKNOWN_ACTION_LOCK_CHANGE_EVENT,
      onSameTabChange,
    );
    window.removeEventListener("storage", onStorage);
  };
}
